import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 24, className = '', as = 'div' }) {
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </M>
  );
}

/** Signature on-load moment: masked line-by-line reveal */
export function MaskedLines({ lines, className = '', lineClassName = '', delay = 0, stagger = 0.14, animate = true }) {
  return (
    <span className={className}>
      {lines.map((l, i) => (
        <span className={`mask-line ${lineClassName}`} key={i}>
          <motion.span
            initial={{ y: animate ? '110%' : 0 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: delay + i * stagger, ease: EASE }}
          >
            {l}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

/** One slow editorial marquee; items rendered once, track duplicated accessibly for a seamless loop */
export function Marquee({ items, className = '' }) {
  const row = (hidden) => (
    <div className="flex shrink-0 items-center" aria-hidden={hidden || undefined}>
      {items.map((it, i) => (
        <span key={`${it}-${i}`} className="flex items-center">
          <span className="px-6 font-mono text-xs sm:text-sm uppercase tracking-[0.25em] whitespace-nowrap">{it}</span>
          <span className="h-1 w-1 rounded-full bg-signal inline-block" />
        </span>
      ))}
    </div>
  );
  return (
    <div className={`marquee-mask ${className}`} data-testid="editorial-marquee">
      <div className="marquee-track">
        {row(false)}
        {row(true)}
      </div>
    </div>
  );
}

/** Deliberate photography: clipped frame, corner ticks, hover zoom, subtle scroll parallax */
export function SpotlightImage({ src, alt, className = '', ratio = 'aspect-[4/3]', parallax = true }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);
  return (
    <div ref={ref} className={`clipped-frame overflow-hidden bg-midnight ${ratio} ${className}`} data-testid="spotlight-image">
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={parallax ? { y } : undefined}
        className="h-full w-full object-cover scale-[1.12] transition-transform duration-700 hover:scale-[1.18]"
      />
    </div>
  );
}

export function Chapter({ n, k, title, body }) {
  return (
    <Reveal className="grid grid-cols-1 md:grid-cols-[110px_1fr] gap-4 md:gap-8 py-8 md:py-10 hairline-b group">
      <div className="font-mono text-sm text-signal tracking-[0.2em] pt-1">{n}</div>
      <div>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h3 className="display text-2xl sm:text-3xl text-ink">{title}</h3>
          <span className="eyebrow text-slate-400 group-hover:text-signal transition-colors duration-300">{k}</span>
        </div>
        <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">{body}</p>
      </div>
    </Reveal>
  );
}
